/**
 *  @swagger
 *  definitions:
 *    RegattaMessageParams:
 *      type: object
 *      properties:
 *        RegattaId:
 *          description: ID of the associated Regatta
 *          type: string
 *        Public:
 *          description: Public of the RegattaMessage
 *          type: boolean
 *        Name:
 *          description: Name of the RegattaMessage
 *          type: string
 *        Type:
 *          description: Type of the RegattaMessage
 *          $ref: '#/components/enums/RegattaMessageType'
 *        Content:
 *          description: Content of the RegattaMessage
 *          type: string
 *        File:
 *          description: File of the RegattaMessage
 *          type: string
 *        PublishDate:
 *          description: Publish Date of the RegattaMessage
 *          type: string
 *          format: date-time
 *
 *  @swagger
 *  definitions:
 *    RegattaMessageCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaMessageParams'
 *        - type: object
 *          required:
 *            - RegattaId
 *            - Public
 *            - Name
 *            - Type
 *            - Content
 *
 *  @swagger
 *  definitions:
 *    RegattaMessageQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the RegattaMessage
 *              type: string
 *        - $ref: '#/definitions/RegattaMessageParams'
 *
 *  @swagger
 *  definitions:
 *    RegattaMessageUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaMessageQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    RegattaMessage:
 *      allOf:
 *        - $ref: '#/definitions/RegattaMessageCreateParams'
 *        - $ref: '#/definitions/RegattaMessageUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      RegattaMessage:
 *        allOf:
 *          - $ref: '#/definitions/RegattaMessage'
 *          - type: object
 *            properties:
 *              Regatta:
 *                description: Regatta of the RegattaMessage
 *                $ref: '#/components/schemas/Regatta'
 */

// Enums
import {
  RegattaMessageType,
} from '../enums';

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  RegattaId: string;
};

export type $Params = {
  Content: string;
  File: null | string;
  Name: string;
  Public: boolean;
  PublishDate?: Date | null;
  Type: RegattaMessageType;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  Regatta?: $Model<'Regatta'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  Content: {
    allowNull: false,
    type: Sequelize.TEXT,
    validate: {
      notEmpty: true,
    },
  },
  File: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Name: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Public: {
    allowNull: false,
    defaultValue: false,
    type: Sequelize.BOOLEAN,
  },
  PublishDate: {
    allowNull: true,
    type: Sequelize.DATE,
    validate: {
      notEmpty: true,
    },
  },
  Type: {
    allowNull: false,
    type: Sequelize.ENUM(...Object.values(RegattaMessageType)),
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'RegattaMessage'> => {
  const RegattaMessage = stack.define(
    'RegattaMessage',
    fields(Sequelize),
    {
      tableName: 'RegattaMessages',
    },
  );

  RegattaMessage.associate = (models: $ModelClasses) => {
    models.RegattaMessage.belongsTo(
      models.Regatta,
      {
        as: 'Regatta',
        foreignKey: 'RegattaId',
      },
    );
  };

  RegattaMessage.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return RegattaMessage;
});
